import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { queryClient, trpc, trpcErrorCode } from "../../app/trpc.js";
import { Button, Card, Chip, Notice, PageTitle, Spinner, Textarea } from "../../ui/index.js";
import { useCabinet } from "./context.js";
import { fmtDateTime } from "./format.js";

type Status = "open" | "answered" | "ignored";

/** «Бот не знал»: ответить (ответ становится знанием бота) или пропустить. */
export function UnknownQuestionsPage() {
  const { t, i18n } = useTranslation();
  const cab = useCabinet();
  const [status, setStatus] = useState<Status>("open");
  const list = useQuery(trpc.bot.unknownQuestions.list.queryOptions({ salonId: cab.salonId, status, limit: 100 }));
  const refresh = () => queryClient.invalidateQueries(trpc.bot.unknownQuestions.list.pathFilter());

  return (
    <div className="lg:max-w-3xl">
      <PageTitle>{t("unknown.title")}</PageTitle>
      <p className="mb-3 text-sm text-slate-600">{t("unknown.hint")}</p>
      <div className="mb-4 flex gap-2 overflow-x-auto lg:flex-wrap">
        {(["open", "answered", "ignored"] as const).map((s) => (
          <Chip key={s} active={status === s} onClick={() => setStatus(s)}>
            {t(`unknown.status.${s}`)}
          </Chip>
        ))}
      </div>
      {list.isPending && <Spinner />}
      {list.isError && <Notice kind="error">{t("common.error")}</Notice>}
      {list.data?.length === 0 && <Notice>{t("unknown.empty")}</Notice>}
      <div className="space-y-3">
        {list.data?.map((q) => (
          <Card key={q.id}>
            <div className="text-xs text-slate-500">{fmtDateTime(new Date(q.created_at), cab.timezone, i18n.language)}</div>
            <div className="mt-1 font-medium">«{q.question}»</div>
            {q.conversation_id && (
              <Link to={`${cab.base}/conversations/${q.conversation_id}`} className="mt-1 inline-block text-sm text-slate-500 underline hover:text-slate-900">
                {t("unknown.openConversation")}
              </Link>
            )}
            {q.status === "open" ? (
              <AnswerForm id={q.id} onDone={refresh} />
            ) : (
              q.answer && <div className="mt-2 rounded-xl bg-slate-50 p-3 text-sm">{q.answer}</div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

function AnswerForm({ id, onDone }: { id: string; onDone: () => void }) {
  const { t } = useTranslation();
  const [answer, setAnswer] = useState("");
  const answerM = useMutation(trpc.bot.unknownQuestions.answer.mutationOptions({ onSuccess: onDone }));
  const ignore = useMutation(trpc.bot.unknownQuestions.ignore.mutationOptions({ onSuccess: onDone }));
  const conflict = trpcErrorCode(answerM.error) === "CONFLICT";
  return (
    <div className="mt-3 space-y-2">
      <Textarea rows={3} value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder={t("unknown.answerPlaceholder")} />
      {answerM.isError && <Notice kind="error">{conflict ? t("unknown.closed") : t("common.error")}</Notice>}
      <div className="flex gap-2">
        <Button className="flex-1" disabled={!answer.trim() || answerM.isPending} onClick={() => answerM.mutate({ id, answer: answer.trim() })}>
          {t("unknown.remember")}
        </Button>
        <Button variant="ghost" disabled={ignore.isPending} onClick={() => ignore.mutate({ id })}>
          {t("unknown.ignore")}
        </Button>
      </div>
    </div>
  );
}
